import {
  endOfMonth,
  endOfQuarter,
  format,
  startOfMonth,
  startOfQuarter,
  subMonths,
  subQuarters,
} from 'date-fns';
import { stripeCurrencyToSymbol } from './stripeCurrency';

const DATE_FMT = 'yyyy-MM-dd';

/**
 * @param {Date} date
 * @returns {string} ISO date YYYY-MM-DD
 */
const toStatementDate = (date) => format(date, DATE_FMT);

/**
 * Preset period options for account statement date ranges.
 * @param {Date} [now]
 * @returns {{ value: string, label: string, from: string, to: string }[]}
 */
const getStatementPeriodPresets = (now = new Date()) => [
  {
    value: 'this_month',
    label: 'This month',
    from: toStatementDate(startOfMonth(now)),
    to: toStatementDate(endOfMonth(now)),
  },
  {
    value: 'last_month',
    label: 'Last month',
    from: toStatementDate(startOfMonth(subMonths(now, 1))),
    to: toStatementDate(endOfMonth(subMonths(now, 1))),
  },
  {
    value: 'last_quarter',
    label: 'Last quarter',
    from: toStatementDate(startOfQuarter(subQuarters(now, 1))),
    to: toStatementDate(endOfQuarter(subQuarters(now, 1))),
  },
  {
    value: 'custom',
    label: 'Custom range',
    from: '',
    to: '',
  },
];

/**
 * Human-readable label for statement line-item types from the billing statement API.
 * Refunds are separate lines (`refund`, `refund_payout`) and do not cancel the invoice row.
 * Unknown types fall through to the raw type string so new backend types still render.
 * @param {string} type
 * @returns {string}
 */
const getLineItemTypeLabel = (type) => {
  if (!type) return '—';
  const labels = {
    invoice: 'Invoice',
    payment: 'Payment',
    credit_note: 'Credit note',
    refund: 'Refund',
    refund_payout: 'Refund paid out',
    adjustment: 'Adjustment',
  };
  return labels[type] || type;
};

/** Statuses that are normal completions — do not surface as hints in the activity table. */
const QUIET_STATEMENT_STATUSES = new Set(['paid', 'succeeded', 'issued']);

/**
 * @param {string} [status]
 * @returns {boolean}
 */
const shouldShowStatementStatus = (status) => !!status && !QUIET_STATEMENT_STATUSES.has(status);

/**
 * Human-readable invoice refund badge from GET /payment/stripe/billing.
 * @param {string} [refundStatus]
 * @returns {string|null}
 */
const getInvoiceRefundBadgeLabel = (refundStatus) => {
  if (refundStatus === 'refunded') return 'Refunded';
  if (refundStatus === 'partially_refunded') return 'Partially refunded';
  return null;
};

/**
 * @param {number|null|undefined} amount
 * @param {string} [currency]
 * @returns {string}
 */
const formatStatementMoney = (amount, currency) => {
  if (amount === null || amount === undefined || Number.isNaN(Number(amount))) return '—';
  const symbol = stripeCurrencyToSymbol(currency);
  const value = Number(amount);
  const formatted = value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return symbol ? `${symbol}${formatted}` : `${formatted} ${String(currency || '').toUpperCase()}`;
};

/**
 * @param {string} [dateStr] — YYYY-MM-DD or ISO datetime
 * @returns {string}
 */
const formatStatementDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr.length === 10 ? `${dateStr}T00:00:00` : dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return format(d, 'dd/MM/yyyy');
};

export {
  DATE_FMT,
  formatStatementDate,
  formatStatementMoney,
  getInvoiceRefundBadgeLabel,
  getLineItemTypeLabel,
  getStatementPeriodPresets,
  shouldShowStatementStatus,
  toStatementDate,
};
